<template>
  <div class="admin-page">
    <admin-page-header
      eyebrow="Kampanyalar"
      title="Kampanya URL sağlığı"
      description="Aktif kampanyaların beklenen hedefini, son açılan URL'yi ve yönlendirme zincirini inceleyin. Bu ekran kampanya durumunu otomatik değiştirmez."
    >
      <template #actions>
        <v-btn
          outlined
          color="primary"
          :loading="loading"
          @click="loadOverview"
        >
          <v-icon left>
            {{ icons.mdiRefresh }}
          </v-icon>
          Yenile
        </v-btn>
      </template>
    </admin-page-header>

    <v-row class="mb-2">
      <v-col
        v-for="card in summaryCards"
        :key="card.key"
        cols="6"
        md="3"
      >
        <v-card
          outlined
          class="url-health-summary pa-4"
        >
          <div class="text-caption grey--text text--darken-1">
            {{ card.label }}
          </div>
          <div :class="['text-h5 font-weight-bold mt-1', `${card.color}--text`]">
            {{ card.value }}
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-alert
      v-if="errorMessage"
      dense
      outlined
      type="error"
      class="mb-4"
    >
      {{ errorMessage }}
    </v-alert>
    <v-alert
      v-if="successMessage"
      dense
      outlined
      type="success"
      class="mb-4"
    >
      {{ successMessage }}
    </v-alert>

    <v-card
      outlined
      class="admin-table-card"
    >
      <div class="admin-table-toolbar url-health-toolbar">
        <v-text-field
          v-model="search"
          outlined
          dense
          clearable
          hide-details
          label="Kampanya, marka veya URL ara"
          :prepend-inner-icon="icons.mdiMagnify"
          class="url-health-toolbar__search"
        ></v-text-field>
        <v-select
          v-model="statusFilter"
          :items="statusOptions"
          item-text="text"
          item-value="value"
          outlined
          dense
          hide-details
          label="URL durumu"
          class="url-health-toolbar__status"
        ></v-select>
      </div>

      <div
        v-if="selectedItems.length"
        class="url-health-bulk-bar"
      >
        <div>
          <div class="font-weight-medium">
            {{ selectedItems.length }} kampanya seçildi
          </div>
          <div class="text-caption grey--text text--darken-1">
            İşlem uygulanmadan önce etkilenecek kampanya ve link sayısı gösterilir.
          </div>
        </div>
        <div class="url-health-bulk-bar__actions">
          <v-btn
            small
            outlined
            color="primary"
            :disabled="operationPreviewLoading || operationExecuting"
            @click="requestOperationPreview('RECHECK', selectedItems, 'bulk')"
          >
            <v-icon
              left
              small
            >
              {{ icons.mdiRefresh }}
            </v-icon>
            Yeniden kontrol et
          </v-btn>
          <v-btn
            small
            outlined
            color="success"
            :disabled="!canMarkSelectedCorrect || operationPreviewLoading || operationExecuting"
            @click="requestOperationPreview('APPROVE', selectedItems, 'bulk')"
          >
            <v-icon
              left
              small
            >
              {{ icons.mdiCheckCircleOutline }}
            </v-icon>
            Doğru işaretle
          </v-btn>
          <v-btn
            small
            color="error"
            :disabled="operationPreviewLoading || operationExecuting"
            @click="requestOperationPreview('COMPLETE', selectedItems, 'bulk')"
          >
            <v-icon
              left
              small
            >
              {{ icons.mdiDeleteOutline }}
            </v-icon>
            Tamamlanmışa çek
          </v-btn>
        </div>
      </div>

      <v-data-table
        v-model="selectedItems"
        :headers="headers"
        :items="filteredItems"
        :loading="loading"
        item-key="campaignId"
        show-select
        :footer-props="{ itemsPerPageOptions: [10, 20, 50, 100] }"
        class="admin-data-table elevation-0 url-health-table"
      >
        <template v-slot:item.campaignName="{ item }">
          <div class="font-weight-medium">
            {{ item.campaignName || `Kampanya #${item.campaignId}` }}
          </div>
          <div class="text-caption grey--text text--darken-1">
            {{ item.brandName || '-' }} · #{{ item.campaignId }}
          </div>
        </template>

        <template v-slot:item.status="{ item }">
          <v-chip
            small
            outlined
            :color="statusColor(item.status)"
          >
            {{ statusLabel(item.status) }}
          </v-chip>
          <div
            v-if="item.consecutiveProblems > 1"
            class="text-caption error--text mt-1"
          >
            {{ item.consecutiveProblems }} ardışık sonuç
          </div>
        </template>

        <template v-slot:item.expectedUrl="{ item }">
          <a
            v-if="item.expectedUrl"
            :href="item.expectedUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="url-health-link"
            :title="item.expectedUrl"
          >
            {{ compactUrl(item.expectedUrl) }}
          </a>
          <span v-else>-</span>
        </template>

        <template v-slot:item.finalUrl="{ item }">
          <a
            v-if="item.finalUrl"
            :href="item.finalUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="url-health-link"
            :title="item.finalUrl"
          >
            {{ compactUrl(item.finalUrl) }}
          </a>
          <span v-else>-</span>
          <v-chip
            v-if="item.approvedFinalUrl"
            x-small
            color="success"
            outlined
            class="ml-1"
          >
            Manuel onay
          </v-chip>
          <div
            v-if="item.approvedFinalUrl"
            class="text-caption"
          >
            {{ formatDateTime(item.approvedAt) }}
          </div>
        </template>

        <template v-slot:item.httpStatus="{ item }">
          <span
            v-if="item.httpStatus"
            title="Son otomatik HTTP kontrolü"
          >HTTP {{ item.httpStatus }}</span>
          <span v-else>-</span>
          <div
            v-if="item.responseTimeMs !== null && item.responseTimeMs !== undefined"
            class="text-caption grey--text"
          >
            {{ item.responseTimeMs }} ms
          </div>
        </template>

        <template v-slot:item.checkedAt="{ item }">
          {{ formatDateTime(item.checkedAt) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="url-health-actions">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  @click="openDetails(item)"
                >
                  <v-icon small>
                    {{ icons.mdiEyeOutline }}
                  </v-icon>
                </v-btn>
              </template>
              <span>Detay ve geçmiş</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="primary"
                  :loading="!!checkingById[item.campaignId]"
                  v-bind="attrs"
                  v-on="on"
                  @click="checkNow(item)"
                >
                  <v-icon small>
                    {{ icons.mdiRefresh }}
                  </v-icon>
                </v-btn>
              </template>
              <span>Şimdi kontrol et</span>
            </v-tooltip>
            <v-tooltip
              v-if="item.canApprove"
              bottom
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="success"
                  :loading="!!approvingById[item.campaignId]"
                  v-bind="attrs"
                  v-on="on"
                  @click="requestOperationPreview('APPROVE', [item], 'row')"
                >
                  <v-icon small>
                    {{ icons.mdiCheckCircleOutline }}
                  </v-icon>
                </v-btn>
              </template>
              <span>Bu yönlendirme doğru</span>
            </v-tooltip>
            <v-tooltip
              v-if="item.approvedFinalUrl"
              bottom
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="warning"
                  :loading="!!approvingById[item.campaignId]"
                  v-bind="attrs"
                  v-on="on"
                  @click="requestClearApproval(item)"
                >
                  <v-icon small>
                    {{ icons.mdiCloseCircleOutline }}
                  </v-icon>
                </v-btn>
              </template>
              <span>Hedef onayını kaldır</span>
            </v-tooltip>
          </div>
        </template>

        <template v-slot:no-data>
          <div class="pa-8 text-center admin-empty-state">
            Filtreye uygun aktif kampanya bulunamadı.
          </div>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog
      v-model="detailDialog"
      max-width="920"
    >
      <v-card v-if="selectedItem">
        <v-toolbar
          flat
          dense
        >
          <v-toolbar-title>{{ selectedItem.campaignName }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="detailDialog = false"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text class="pt-5">
          <v-chip
            small
            outlined
            :color="statusColor(selectedItem.status)"
            class="mb-4"
          >
            {{ statusLabel(selectedItem.status) }}
          </v-chip>

          <v-alert
            dense
            outlined
            :type="classificationMeta(selectedItem.status).type"
            class="url-health-classification"
          >
            <div class="font-weight-medium">
              {{ classificationMeta(selectedItem.status).title }}
            </div>
            <div class="text-body-2 mt-1">
              {{ classificationMeta(selectedItem.status).description }}
            </div>
            <div
              v-if="selectedItem.reason"
              class="text-caption mt-1"
            >
              Kontrol sonucu: {{ selectedItem.reason }}
            </div>
          </v-alert>

          <div class="url-health-detail-grid">
            <div>
              <div class="text-caption grey--text">
                Beklenen URL
              </div>
              <a
                :href="selectedItem.expectedUrl"
                target="_blank"
                rel="noopener noreferrer"
              >{{ selectedItem.expectedUrl || '-' }}</a>
            </div>
            <div>
              <div class="text-caption grey--text">
                Son URL
              </div>
              <a
                v-if="selectedItem.finalUrl"
                :href="selectedItem.finalUrl"
                target="_blank"
                rel="noopener noreferrer"
              >{{ selectedItem.finalUrl }}</a>
              <span v-else>-</span>
            </div>
            <div v-if="selectedItem.approvedFinalUrl">
              <div class="text-caption grey--text">
                Onaylı hedef
              </div>
              <span>{{ selectedItem.approvedFinalUrl }}</span>
            </div>
            <div>
              <div class="text-caption grey--text">
                Sayfa başlığı
              </div>
              <span>{{ selectedItem.pageTitle || '-' }}</span>
            </div>
            <div>
              <div class="text-caption grey--text">
                Sınıflandırma kodu
              </div>
              <span>{{ selectedItem.status || '-' }}</span>
            </div>
            <div>
              <div class="text-caption grey--text">
                Otomatik HTTP kontrolü / yanıt süresi
              </div>
              <span>{{ selectedItem.httpStatus ? `HTTP ${selectedItem.httpStatus}` : '-' }} · {{ selectedItem.responseTimeMs !== null && selectedItem.responseTimeMs !== undefined ? `${selectedItem.responseTimeMs} ms` : '-' }}</span>
            </div>
            <div>
              <div class="text-caption grey--text">
                Ardışık problem
              </div>
              <span>{{ selectedItem.consecutiveProblems || 0 }}</span>
            </div>
            <div>
              <div class="text-caption grey--text">
                Son otomatik kontrol
              </div>
              <span>{{ formatDateTime(selectedItem.checkedAt) }}</span>
            </div>
          </div>

          <v-divider class="my-5"></v-divider>
          <div class="text-subtitle-2 font-weight-medium mb-1">
            Manuel işlemler
          </div>
          <div class="text-caption grey--text text--darken-1 mb-3">
            URL güncellemesi kampanyayı ve bu kampanyaya bağlı mevcut linklerin hedefini birlikte yeniler.
          </div>
          <v-text-field
            v-model="newCampaignUrl"
            outlined
            dense
            label="Yeni kampanya URL'si"
            placeholder="https://"
            :error-messages="newCampaignUrlError ? [newCampaignUrlError] : []"
            class="mb-1"
            @input="newCampaignUrlTouched = true"
          ></v-text-field>
          <div class="url-health-manual-actions">
            <v-btn
              color="primary"
              outlined
              :disabled="!canUpdateSelectedUrl || operationPreviewLoading || operationExecuting"
              @click="requestUrlUpdate"
            >
              <v-icon
                left
                small
              >
                {{ icons.mdiLinkVariant }}
              </v-icon>
              URL'yi güncelle
            </v-btn>
            <v-btn
              color="success"
              outlined
              :disabled="!selectedItem.canApprove || operationPreviewLoading || operationExecuting"
              @click="requestOperationPreview('APPROVE', [selectedItem], 'detail')"
            >
              <v-icon
                left
                small
              >
                {{ icons.mdiCheckCircleOutline }}
              </v-icon>
              Bu link doğru
            </v-btn>
            <v-btn
              color="error"
              :disabled="operationPreviewLoading || operationExecuting"
              @click="requestOperationPreview('COMPLETE', [selectedItem], 'detail')"
            >
              <v-icon
                left
                small
              >
                {{ icons.mdiDeleteOutline }}
              </v-icon>
              Kampanyayı tamamla
            </v-btn>
          </div>

          <v-divider class="my-5"></v-divider>
          <div class="text-subtitle-2 font-weight-medium mb-3">
            Redirect zinciri
          </div>
          <v-timeline
            v-if="selectedItem.redirectChain && selectedItem.redirectChain.length"
            dense
            align-top
          >
            <v-timeline-item
              v-for="(url, index) in selectedItem.redirectChain"
              :key="`${url}-${index}`"
              small
              :color="index === selectedItem.redirectChain.length - 1 ? statusColor(selectedItem.status) : 'primary'"
            >
              <div class="text-caption grey--text">
                {{ index === 0 ? 'Başlangıç' : `Adım ${index}` }}
              </div>
              <a
                :href="url"
                target="_blank"
                rel="noopener noreferrer"
                class="url-health-detail-url"
              >{{ url }}</a>
            </v-timeline-item>
          </v-timeline>
          <div
            v-else
            class="text-body-2 grey--text"
          >
            Redirect zinciri kaydedilmemiş.
          </div>

          <v-divider class="my-5"></v-divider>
          <div class="text-subtitle-2 font-weight-medium mb-3">
            Son kontroller
          </div>
          <v-data-table
            dense
            :headers="historyHeaders"
            :items="history"
            :loading="historyLoading"
            hide-default-footer
            class="elevation-0"
          >
            <template v-slot:item.status="{ item }">
              <v-chip
                x-small
                outlined
                :color="statusColor(item.status)"
              >
                {{ statusLabel(item.status) }}
              </v-chip>
            </template>
            <template v-slot:item.triggerSource="{ item }">
              {{ item.triggerSource === 'MANUAL' ? 'Manuel' : 'Cron' }}
            </template>
            <template v-slot:item.checkedAt="{ item }">
              {{ formatDateTime(item.checkedAt) }}
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="operationPreviewDialog"
      max-width="620"
      persistent
    >
      <v-card>
        <v-card-title class="text-h6">
          {{ operationDefinition(pendingOperation.action).title }}
        </v-card-title>
        <v-card-text>
          <v-progress-linear
            v-if="operationPreviewLoading"
            indeterminate
            color="primary"
            class="mb-5"
          ></v-progress-linear>
          <template v-else>
            <p class="mb-4">
              {{ operationDefinition(pendingOperation.action).description }}
            </p>

            <div class="url-health-preview-grid">
              <v-card
                outlined
                class="pa-4"
              >
                <div class="text-caption grey--text text--darken-1">
                  Etkilenecek kampanya
                </div>
                <div class="text-h5 font-weight-bold mt-1">
                  {{ operationPreview.campaignCount }}
                </div>
              </v-card>
              <v-card
                outlined
                class="pa-4"
              >
                <div class="text-caption grey--text text--darken-1">
                  Etkilenecek bağlı link
                </div>
                <div class="text-h5 font-weight-bold mt-1">
                  {{ operationPreview.affectedLinkCount }}
                </div>
              </v-card>
            </div>

            <v-alert
              v-if="pendingOperation.action === 'COMPLETE'"
              dense
              outlined
              type="warning"
              class="mt-4 mb-0"
            >
              Kampanyalar tamamlanmışa, bağlı aktif linkler de tamamlanmış duruma çekilecek ve bu listeden kaldırılacak.
            </v-alert>

            <div
              v-if="pendingOperation.action === 'UPDATE_URL'"
              class="url-health-preview-url mt-4"
            >
              <div class="text-caption grey--text">
                Yeni hedef URL
              </div>
              <strong>{{ pendingOperation.newUrl }}</strong>
            </div>

            <div
              v-if="pendingOperation.items.length"
              class="mt-4"
            >
              <div class="text-caption grey--text mb-1">
                Seçilen kayıtlar
              </div>
              <div class="text-body-2">
                {{ previewCampaignNames }}
              </div>
            </div>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            :disabled="operationExecuting"
            @click="closeOperationPreview"
          >
            İptal
          </v-btn>
          <v-btn
            :color="operationDefinition(pendingOperation.action).color"
            :loading="operationExecuting"
            :disabled="operationPreviewLoading"
            @click="executePendingOperation"
          >
            {{ operationDefinition(pendingOperation.action).confirmText }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="confirmDialog"
      max-width="520"
    >
      <v-card>
        <v-card-title class="text-h6">
          {{ confirmMode === 'approve' ? 'Yönlendirme hedefini onayla' : 'Hedef onayını kaldır' }}
        </v-card-title>
        <v-card-text>
          {{ confirmMode === 'approve'
            ? 'Açılan son URL bu kampanya için doğru hedef olarak kaydedilecek.'
            : 'Onaylı hedef kaldırılacak ve kampanya yeniden kontrol bekleyecek.' }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeConfirmation"
          >
            İptal
          </v-btn>
          <v-btn
            :color="confirmMode === 'approve' ? 'success' : 'warning'"
            :loading="confirmItem && !!approvingById[confirmItem.campaignId]"
            @click="confirmHealthAction"
          >
            {{ confirmMode === 'approve' ? 'Doğru hedef olarak onayla' : 'Onayı kaldır' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import {
  mdiCheckCircleOutline,
  mdiClose,
  mdiCloseCircleOutline,
  mdiDeleteOutline,
  mdiEyeOutline,
  mdiLinkVariant,
  mdiMagnify,
  mdiRefresh,
} from '@mdi/js'
import AdminPageHeader from '@/components/admin/AdminPageHeader.vue'
import { getAccessToken } from '@/utils/panelSession'
import { isAuthError, resolveApiErrorMessage } from '@/utils/apiErrors'

export default {
  name: 'CampaignUrlHealth',
  components: { AdminPageHeader },
  data: () => ({
    icons: {
      mdiCheckCircleOutline,
      mdiClose,
      mdiCloseCircleOutline,
      mdiDeleteOutline,
      mdiEyeOutline,
      mdiLinkVariant,
      mdiMagnify,
      mdiRefresh,
    },
    items: [],
    selectedItems: [],
    statusCounts: {},
    totalActiveCampaigns: 0,
    loading: false,
    errorMessage: '',
    successMessage: '',
    search: '',
    statusFilter: 'ALL',
    checkingById: {},
    approvingById: {},
    detailDialog: false,
    selectedItem: null,
    newCampaignUrl: '',
    newCampaignUrlTouched: false,
    history: [],
    historyLoading: false,
    operationPreviewDialog: false,
    operationPreviewLoading: false,
    operationExecuting: false,
    operationPreview: {
      campaignCount: 0,
      affectedLinkCount: 0,
      action: '',
    },
    pendingOperation: {
      action: '',
      items: [],
      source: '',
      newUrl: '',
    },
    confirmDialog: false,
    confirmMode: '',
    confirmItem: null,
    statusOptions: [
      { text: 'Tümü', value: 'ALL' },
      { text: 'Manuel inceleme', value: 'REVIEW_REDIRECT' },
      { text: 'Ana sayfaya yönlendi', value: 'HOME_REDIRECT' },
      { text: 'Soft 404', value: 'SOFT_404' },
      { text: 'Bozuk', value: 'BROKEN' },
      { text: 'Geçici hata', value: 'TEMPORARY_ERROR' },
      { text: 'Erişim engeli', value: 'BLOCKED' },
      { text: 'Geçersiz URL', value: 'INVALID_URL' },
      { text: 'Güvenlik engeli', value: 'SECURITY_BLOCKED' },
      { text: 'Sağlıklı yönlendirme', value: 'HEALTHY_REDIRECT' },
      { text: 'Sağlıklı', value: 'HEALTHY' },
      { text: 'Kontrol edilmedi', value: 'UNCHECKED' },
    ],
    headers: [
      { text: 'Kampanya', value: 'campaignName', width: 250 },
      { text: 'URL Durumu', value: 'status', width: 190 },
      {
        text: 'Beklenen URL', value: 'expectedUrl', sortable: false, width: 260,
      },
      {
        text: 'Son URL', value: 'finalUrl', sortable: false, width: 260,
      },
      { text: 'Yanıt', value: 'httpStatus', width: 120 },
      { text: 'Son Kontrol', value: 'checkedAt', width: 180 },
      {
        text: 'İşlemler', value: 'actions', sortable: false, width: 170,
      },
    ],
    historyHeaders: [
      { text: 'Tarih', value: 'checkedAt', width: 180 },
      { text: 'Kaynak', value: 'triggerSource', width: 100 },
      { text: 'Durum', value: 'status', width: 170 },
      { text: 'HTTP', value: 'httpStatus', width: 80 },
      { text: 'Açıklama', value: 'reason', sortable: false },
    ],
  }),
  computed: {
    requestOptions() {
      return { headers: { Authorization: `Bearer ${getAccessToken()}` } }
    },
    filteredItems() {
      const needle = String(this.search || '').trim().toLocaleLowerCase('tr-TR')

      return this.items.filter(item => {
        if (this.statusFilter !== 'ALL' && item.status !== this.statusFilter) return false
        if (!needle) return true
        const haystack = [item.campaignId, item.campaignName, item.brandName, item.expectedUrl, item.finalUrl]
          .filter(value => value !== null && value !== undefined)
          .join(' ')
          .toLocaleLowerCase('tr-TR')

        return haystack.includes(needle)
      })
    },
    canMarkSelectedCorrect() {
      return this.selectedItems.length > 0 && this.selectedItems.every(item => Boolean(item.canApprove))
    },
    newCampaignUrlError() {
      if (!this.newCampaignUrlTouched) return ''

      return this.validateCampaignUrl(this.newCampaignUrl)
    },
    canUpdateSelectedUrl() {
      if (!this.selectedItem || this.validateCampaignUrl(this.newCampaignUrl)) return false

      return String(this.newCampaignUrl || '').trim() !== String(this.selectedItem.expectedUrl || '').trim()
    },
    previewCampaignNames() {
      const names = this.pendingOperation.items.map(item => (
        item.campaignName || `Kampanya #${item.campaignId}`
      ))
      const visibleNames = names.slice(0, 4).join(', ')
      const remaining = names.length - 4

      return remaining > 0 ? `${visibleNames} ve ${remaining} kampanya daha` : visibleNames
    },
    summaryCards() {
      const healthy = Number(this.statusCounts.HEALTHY || 0) + Number(this.statusCounts.HEALTHY_REDIRECT || 0)
      const review = Number(this.statusCounts.REVIEW_REDIRECT || 0)
      const broken = ['HOME_REDIRECT', 'SOFT_404', 'BROKEN', 'INVALID_URL', 'SECURITY_BLOCKED']
        .reduce((sum, key) => sum + Number(this.statusCounts[key] || 0), 0)

      return [
        {
          key: 'total', label: 'Aktif kampanya', value: this.totalActiveCampaigns, color: 'primary',
        },
        {
          key: 'healthy', label: 'Sağlıklı', value: healthy, color: 'success',
        },
        {
          key: 'review', label: 'Manuel inceleme', value: review, color: 'warning',
        },
        {
          key: 'broken', label: 'Bozuk / şüpheli', value: broken, color: 'error',
        },
      ]
    },
  },
  mounted() {
    if (this.$route.query.campaignId) {
      this.search = String(this.$route.query.campaignId)
    }
    this.loadOverview()
  },
  methods: {
    async loadOverview() {
      this.loading = true
      this.errorMessage = ''
      try {
        const selectedIds = new Set(this.selectedItems.map(item => String(item.campaignId)))
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}admin/campaign-url-health`,
          this.requestOptions,
        )
        const payload = response?.data?.data || {}
        this.items = Array.isArray(payload.items) ? payload.items : []
        this.selectedItems = this.items.filter(item => selectedIds.has(String(item.campaignId)))
        this.statusCounts = payload.statusCounts || {}
        this.totalActiveCampaigns = Number(payload.totalActiveCampaigns || this.items.length)
        if (this.selectedItem) {
          const refreshedItem = this.items.find(item => (
            String(item.campaignId) === String(this.selectedItem.campaignId)
          ))
          if (refreshedItem) {
            this.selectedItem = refreshedItem
          } else {
            this.detailDialog = false
            this.selectedItem = null
          }
        }
      } catch (error) {
        if (isAuthError(error)) {
          this.$router.push({ name: 'login' })

          return
        }
        this.errorMessage = resolveApiErrorMessage(error, 'Kampanya URL sağlığı yüklenemedi.')
      } finally {
        this.loading = false
      }
    },
    async checkNow(item) {
      this.$set(this.checkingById, item.campaignId, true)
      this.errorMessage = ''
      this.successMessage = ''
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BASE_API_URL}admin/campaign-url-health/${item.campaignId}/check`,
          {},
          this.requestOptions,
        )
        this.replaceItem(response?.data?.data)
        this.successMessage = response?.data?.message || 'URL kontrolü tamamlandı.'
      } catch (error) {
        this.errorMessage = resolveApiErrorMessage(error, 'URL kontrolü tamamlanamadı.')
      } finally {
        this.$set(this.checkingById, item.campaignId, false)
      }
    },
    requestApproveTarget(item) {
      this.requestOperationPreview('APPROVE', [item], 'row')
    },
    requestClearApproval(item) {
      this.confirmMode = 'clear'
      this.confirmItem = item
      this.confirmDialog = true
    },
    closeConfirmation() {
      this.confirmDialog = false
      this.confirmMode = ''
      this.confirmItem = null
    },
    async confirmHealthAction() {
      if (!this.confirmItem) return
      if (this.confirmMode === 'approve') {
        await this.approveTarget(this.confirmItem)
      } else {
        await this.clearApproval(this.confirmItem)
      }
      this.closeConfirmation()
    },
    async approveTarget(item) {
      this.$set(this.approvingById, item.campaignId, true)
      this.errorMessage = ''
      this.successMessage = ''
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BASE_API_URL}admin/campaign-url-health/${item.campaignId}/approve-current-target`,
          {},
          this.requestOptions,
        )
        this.replaceItem(response?.data?.data)
        this.successMessage = response?.data?.message || 'Yönlendirme hedefi onaylandı.'
      } catch (error) {
        this.errorMessage = resolveApiErrorMessage(error, 'Yönlendirme hedefi onaylanamadı.')
      } finally {
        this.$set(this.approvingById, item.campaignId, false)
      }
    },
    async clearApproval(item) {
      this.$set(this.approvingById, item.campaignId, true)
      this.errorMessage = ''
      this.successMessage = ''
      try {
        const response = await axios.delete(
          `${process.env.VUE_APP_BASE_API_URL}admin/campaign-url-health/${item.campaignId}/approval`,
          this.requestOptions,
        )
        this.replaceItem(response?.data?.data)
        this.successMessage = response?.data?.message || 'Yönlendirme hedefi onayı kaldırıldı.'
      } catch (error) {
        this.errorMessage = resolveApiErrorMessage(error, 'Yönlendirme hedefi onayı kaldırılamadı.')
      } finally {
        this.$set(this.approvingById, item.campaignId, false)
      }
    },
    replaceItem(nextItem) {
      if (!nextItem || !nextItem.campaignId) return
      const index = this.items.findIndex(item => String(item.campaignId) === String(nextItem.campaignId))
      if (index >= 0) this.$set(this.items, index, nextItem)
      if (this.selectedItem && String(this.selectedItem.campaignId) === String(nextItem.campaignId)) {
        this.selectedItem = nextItem
      }
      this.refreshLocalCounts()
    },
    refreshLocalCounts() {
      this.statusCounts = this.items.reduce((counts, item) => ({
        ...counts,
        [item.status]: Number(counts[item.status] || 0) + 1,
      }), {})
    },
    async openDetails(item) {
      this.selectedItem = item
      this.newCampaignUrl = item.expectedUrl || ''
      this.newCampaignUrlTouched = false
      this.detailDialog = true
      this.history = []
      this.historyLoading = true
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_BASE_API_URL}admin/campaign-url-health/${item.campaignId}/history`,
          { ...this.requestOptions, params: { limit: 20 } },
        )
        this.history = Array.isArray(response?.data?.data) ? response.data.data : []
      } catch (error) {
        this.errorMessage = resolveApiErrorMessage(error, 'URL kontrol geçmişi yüklenemedi.')
      } finally {
        this.historyLoading = false
      }
    },
    validateCampaignUrl(value) {
      const normalized = String(value || '').trim()
      if (!normalized) return 'Yeni kampanya URL\'si zorunludur.'
      try {
        const parsed = new URL(normalized)
        if (!['http:', 'https:'].includes(parsed.protocol) || !parsed.hostname) {
          return 'Geçerli bir http veya https URL girin.'
        }
      } catch (error) {
        return 'Geçerli bir URL girin.'
      }

      return ''
    },
    requestUrlUpdate() {
      this.newCampaignUrlTouched = true
      if (!this.canUpdateSelectedUrl) return
      this.requestOperationPreview('UPDATE_URL', [this.selectedItem], 'detail', {
        newUrl: String(this.newCampaignUrl).trim(),
      })
    },
    normalizeOperationItems(items) {
      const uniqueItems = new Map()
      const sourceItems = Array.isArray(items) ? items : []
      sourceItems.forEach(item => {
        if (item && item.campaignId !== null && item.campaignId !== undefined) {
          uniqueItems.set(String(item.campaignId), item)
        }
      })

      return Array.from(uniqueItems.values())
    },
    async requestOperationPreview(action, items, source, options = {}) {
      const normalizedItems = this.normalizeOperationItems(items)
      if (!normalizedItems.length) return

      this.errorMessage = ''
      this.successMessage = ''
      this.pendingOperation = {
        action,
        items: normalizedItems,
        source,
        newUrl: options.newUrl || '',
      }
      this.operationPreview = {
        campaignCount: normalizedItems.length,
        affectedLinkCount: 0,
        action,
      }
      this.operationPreviewDialog = true
      this.operationPreviewLoading = true
      try {
        const response = await axios.post(
          `${process.env.VUE_APP_BASE_API_URL}admin/campaign-url-health/action-preview`,
          {
            campaignIds: normalizedItems.map(item => item.campaignId),
            action,
          },
          this.requestOptions,
        )
        const payload = response?.data?.data || {}
        this.operationPreview = {
          campaignCount: Number(payload.campaignCount ?? normalizedItems.length),
          affectedLinkCount: Number(payload.affectedLinkCount ?? 0),
          action: payload.action || action,
        }
      } catch (error) {
        this.operationPreviewDialog = false
        if (isAuthError(error)) {
          this.$router.push({ name: 'login' })

          return
        }
        this.errorMessage = resolveApiErrorMessage(error, 'İşlem etkisi hesaplanamadı.')
      } finally {
        this.operationPreviewLoading = false
      }
    },
    closeOperationPreview() {
      if (this.operationExecuting) return
      this.operationPreviewDialog = false
      this.operationPreview = {
        campaignCount: 0,
        affectedLinkCount: 0,
        action: '',
      }
      this.pendingOperation = {
        action: '',
        items: [],
        source: '',
        newUrl: '',
      }
    },
    async executePendingOperation() {
      const operation = this.pendingOperation
      if (!operation.action || !operation.items.length) return

      this.operationExecuting = true
      this.errorMessage = ''
      this.successMessage = ''
      try {
        let response
        if (operation.action === 'UPDATE_URL') {
          const [item] = operation.items
          response = await axios.put(
            `${process.env.VUE_APP_BASE_API_URL}admin/campaign-url-health/${item.campaignId}/url`,
            { url: operation.newUrl },
            this.requestOptions,
          )
          const updatedItem = response?.data?.data
          this.replaceItem(updatedItem)
          this.newCampaignUrl = updatedItem?.expectedUrl || operation.newUrl
          this.newCampaignUrlTouched = false
        } else {
          response = await axios.post(
            `${process.env.VUE_APP_BASE_API_URL}admin/campaign-url-health/bulk-action`,
            {
              campaignIds: operation.items.map(item => item.campaignId),
              action: operation.action,
            },
            this.requestOptions,
          )
          if (operation.action === 'COMPLETE') {
            const completedIds = new Set(operation.items.map(item => String(item.campaignId)))
            this.items = this.items.filter(item => !completedIds.has(String(item.campaignId)))
            this.selectedItems = this.selectedItems.filter(item => !completedIds.has(String(item.campaignId)))
            if (this.selectedItem && completedIds.has(String(this.selectedItem.campaignId))) {
              this.detailDialog = false
              this.selectedItem = null
            }
          }
          await this.loadOverview()
        }

        const definition = this.operationDefinition(operation.action)
        this.successMessage = response?.data?.message || definition.successMessage
        this.selectedItems = []
        this.operationExecuting = false
        this.closeOperationPreview()
      } catch (error) {
        if (isAuthError(error)) {
          this.$router.push({ name: 'login' })

          return
        }
        this.errorMessage = resolveApiErrorMessage(error, 'İşlem tamamlanamadı.')
      } finally {
        this.operationExecuting = false
      }
    },
    operationDefinition(action) {
      const definitions = {
        RECHECK: {
          title: 'URL kontrolünü yeniden çalıştır',
          description: 'Seçilen kampanyaların hedef URL kontrolleri şimdi yeniden çalıştırılacak.',
          confirmText: 'Kontrolü başlat',
          successMessage: 'URL kontrolleri tamamlandı.',
          color: 'primary',
        },
        APPROVE: {
          title: 'Bu link doğru',
          description: 'Son açılan URL seçilen kampanyalar için doğru hedef olarak onaylanacak.',
          confirmText: 'Doğru işaretle',
          successMessage: 'Seçilen yönlendirme hedefleri doğru olarak işaretlendi.',
          color: 'success',
        },
        COMPLETE: {
          title: 'Kampanyaları tamamlanmışa çek',
          description: 'Seçilen kampanyalar ve bunlara bağlı aktif linkler tamamlanmış duruma geçirilecek.',
          confirmText: 'Tamamlanmışa çek',
          successMessage: 'Seçilen kampanyalar tamamlanmışa çekildi.',
          color: 'error',
        },
        UPDATE_URL: {
          title: 'Kampanya URL\'sini güncelle',
          description: 'Kampanya hedefi ve kampanyaya bağlı linklerin yönlendirme hedefi birlikte güncellenecek.',
          confirmText: 'URL\'yi güncelle',
          successMessage: 'Kampanya URL\'si ve bağlı link hedefleri güncellendi.',
          color: 'primary',
        },
      }

      return definitions[action] || {
        title: 'İşlemi onayla',
        description: 'Seçilen kayıtlar için işlem uygulanacak.',
        confirmText: 'Onayla',
        successMessage: 'İşlem tamamlandı.',
        color: 'primary',
      }
    },
    classificationMeta(status) {
      const classifications = {
        UNCHECKED: {
          type: 'info', title: 'Henüz kontrol edilmedi', description: 'Kampanya URL\'si için kayıtlı bir sağlık kontrolü bulunmuyor.',
        },
        HEALTHY: {
          type: 'success', title: 'Doğrudan sağlıklı', description: 'Beklenen kampanya URL\'si geçerli içerikle doğrudan açıldı.',
        },
        HEALTHY_REDIRECT: {
          type: 'success', title: 'Sağlıklı yönlendirme', description: 'Yönlendirme zinciri kabul edilebilir ve son hedef kampanya içeriğiyle uyumlu.',
        },
        REVIEW_REDIRECT: {
          type: 'warning', title: 'Manuel yönlendirme incelemesi', description: 'Son hedef açılıyor ancak kampanya hedefiyle eşdeğer olduğu otomatik olarak doğrulanamadı.',
        },
        HOME_REDIRECT: {
          type: 'error', title: 'Ana sayfaya yönlenme', description: 'Kampanya URL\'si ürün veya kampanya içeriği yerine sitenin ana sayfasına ulaşıyor.',
        },
        SOFT_404: {
          type: 'error', title: 'Soft 404', description: 'Sayfa HTTP 200 dönse de içerik kampanyanın veya sayfanın artık bulunmadığını gösteriyor.',
        },
        BROKEN: {
          type: 'error', title: 'Bozuk hedef', description: 'URL kalıcı hata verdi veya geçerli bir hedefe ulaşamadı.',
        },
        TEMPORARY_ERROR: {
          type: 'warning', title: 'Geçici erişim hatası', description: 'Timeout veya geçici sunucu hatası görüldü; yeniden kontrol edilmesi gerekir.',
        },
        BLOCKED: {
          type: 'warning', title: 'Erişim engeli', description: 'Hedef site otomatik sağlık kontrolü isteğini engelledi.',
        },
        INVALID_URL: {
          type: 'error', title: 'Geçersiz URL', description: 'Kayıtlı kampanya URL\'si geçerli bir HTTP veya HTTPS adresi değil.',
        },
        SECURITY_BLOCKED: {
          type: 'error', title: 'Güvenlik nedeniyle engellendi', description: 'URL güvenli olmayan veya izin verilmeyen bir hedefe yönlendiği için kontrol durduruldu.',
        },
      }

      return classifications[status] || {
        type: 'info', title: this.statusLabel(status), description: 'Kontrol sonucu için ek sınıflandırma bilgisi bulunmuyor.',
      }
    },
    statusLabel(status) {
      const labels = {
        UNCHECKED: 'Kontrol edilmedi',
        HEALTHY: 'Sağlıklı',
        HEALTHY_REDIRECT: 'Sağlıklı yönlendirme',
        REVIEW_REDIRECT: 'Manuel inceleme',
        HOME_REDIRECT: 'Ana sayfaya yönlendi',
        SOFT_404: 'Soft 404',
        BROKEN: 'Bozuk',
        TEMPORARY_ERROR: 'Geçici hata',
        BLOCKED: 'Erişim engeli',
        INVALID_URL: 'Geçersiz URL',
        SECURITY_BLOCKED: 'Güvenlik engeli',
      }

      return labels[status] || status || '-'
    },
    statusColor(status) {
      if (status === 'HEALTHY' || status === 'HEALTHY_REDIRECT') return 'success'
      if (status === 'REVIEW_REDIRECT' || status === 'TEMPORARY_ERROR' || status === 'BLOCKED') return 'warning'
      if (status === 'UNCHECKED') return 'secondary'

      return 'error'
    },
    compactUrl(value) {
      if (!value) return '-'
      try {
        const parsed = new URL(value)
        const compact = `${parsed.host}${parsed.pathname}${parsed.search}`

        return compact.length > 62 ? `${compact.slice(0, 59)}...` : compact
      } catch (error) {
        return value.length > 62 ? `${value.slice(0, 59)}...` : value
      }
    },
    formatDateTime(value) {
      if (!value) return '-'
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return value

      return parsed.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.url-health-summary {
  height: 100%;
  border-radius: 16px;
}

.url-health-toolbar {
  display: flex;
  gap: 16px;
  align-items: center;
}

.url-health-toolbar__search {
  max-width: 520px;
}

.url-health-toolbar__status {
  max-width: 260px;
}

.url-health-bulk-bar {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) auto;
  gap: 20px;
  align-items: center;
  padding: 14px 20px;
  border-top: 1px solid rgba(94, 86, 103, 0.12);
  background: rgba(225, 29, 72, 0.035);
}

.url-health-bulk-bar__actions,
.url-health-manual-actions {
  display: grid;
  grid-auto-flow: column;
  gap: 10px;
  justify-content: end;
}

.url-health-link {
  display: inline-block;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}

.url-health-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.url-health-detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.url-health-classification {
  margin-bottom: 20px;
}

.url-health-preview-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.url-health-preview-grid .v-card {
  border-radius: 12px;
}

.url-health-preview-url {
  padding: 12px 14px;
  border: 1px solid rgba(94, 86, 103, 0.16);
  border-radius: 10px;
  overflow-wrap: anywhere;
}

.url-health-detail-grid a,
.url-health-detail-grid span,
.url-health-detail-url {
  overflow-wrap: anywhere;
}

@media (max-width: 760px) {
  .url-health-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .url-health-toolbar__search,
  .url-health-toolbar__status {
    max-width: none;
  }

  .url-health-bulk-bar {
    grid-template-columns: 1fr;
  }

  .url-health-bulk-bar__actions,
  .url-health-manual-actions {
    grid-auto-flow: row;
    justify-content: stretch;
  }

  .url-health-detail-grid,
  .url-health-preview-grid {
    grid-template-columns: 1fr;
  }
}
</style>
